import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Eye, Upload, Building2, Download, UserPlus } from "lucide-react";
import BulkImportPreview from "@/components/BulkImportPreview";

export default function Interns({ user }) {
  const [interns, setInterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentIntern, setCurrentIntern] = useState(null);
  
  // Department assignment dialog
  const [deptAssignmentDialogOpen, setDeptAssignmentDialogOpen] = useState(false);
  const [selectedIntern, setSelectedIntern] = useState(null);
  const [departmentHistory, setDepartmentHistory] = useState([]);
  
  // Edit department assignment (team only)
  const [editDeptDialogOpen, setEditDeptDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editTeamId, setEditTeamId] = useState("");
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingIntern, setViewingIntern] = useState(null);
  
  // Bulk import with preview states
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);
  
  // Bulk upload
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccessCount, setUploadSuccessCount] = useState(0);
  
  // Convert to employee
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertingIntern, setConvertingIntern] = useState(null);
  const [employeeId, setEmployeeId] = useState("");
  
  // Filters
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  
  // Tab state
  const [activeTab, setActiveTab] = useState("active");
  
  // Employees for manager dropdown
  const [employees, setEmployees] = useState([]);
  
  // Branches from API
  const [branches, setBranches] = useState([]);
  
  // Teams for team dropdown
  const [teams, setTeams] = useState([]);
  
  const [formData, setFormData] = useState({
    intern_number: "",
    first_name: "",
    last_name: "",
    pseudo_first_name: "",
    pseudo_last_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    branch: "",
    batch_number: "",
    intern_start_date: "",
    intern_end_date: "",
    completion_status: "Active",
    manager_id: ""
  });
  
  const [deptAssignmentData, setDeptAssignmentData] = useState({
    department: "",
    team_id: "",
    start_date: "",
    notes: ""
  });
  
  const statuses = ["Active", "Completed", "Released", "Absconded"];
  const departments = ["Training", "Sales", "Estimation", "Project", "Finance", "Management", "HR"];

  useEffect(() => {
    fetchInterns();
    fetchEmployees();
    fetchBranches();
    fetchTeams();
  }, [selectedBatch, selectedBranch, selectedStatus]);

  const fetchBranches = async () => {
    try {
      const response = await api.get("/branches");
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const fetchInterns = async () => {
    try {
      setLoading(true);
      let url = "/interns";
      const params = [];
      if (selectedBatch) params.push(`batch=${selectedBatch}`);
      if (selectedBranch) params.push(`branch=${selectedBranch}`);
      if (selectedStatus) params.push(`status=${selectedStatus}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const response = await api.get(url);
      setInterns(response.data);
    } catch (error) {
      console.error("Failed to fetch interns:", error);
      toast.error("Failed to fetch interns");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get("/teams");
      setTeams(response.data || []);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
      setTeams([]);
    }
  };

  const handleCreate = () => {
    setEditMode(false);
    setCurrentIntern(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleEdit = (intern) => {
    setEditMode(true);
    setCurrentIntern(intern);
    setFormData({
      intern_number: intern.intern_number || "",
      first_name: intern.first_name || "",
      last_name: intern.last_name || "",
      pseudo_first_name: intern.pseudo_first_name || "",
      pseudo_last_name: intern.pseudo_last_name || "",
      email: intern.email || "",
      phone: intern.phone || "",
      date_of_birth: intern.date_of_birth || "",
      branch: intern.branch || "",
      batch_number: intern.batch_number || "",
      intern_start_date: intern.intern_start_date || "",
      intern_end_date: intern.intern_end_date || "",
      completion_status: intern.completion_status || "Active",
      manager_id: intern.manager_id || ""
    });
    setDialogOpen(true);
  };

  const handleView = (intern) => {
    setViewingIntern(intern);
    setViewDialogOpen(true);
    fetchDepartmentHistory(intern.id);
  };

  const handleDelete = async (internId) => {
    if (!window.confirm("Are you sure you want to delete this intern?")) return;
    
    try {
      await api.delete(`/interns/${internId}`);
      toast.success("Intern deleted successfully");
      fetchInterns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete intern");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.intern_number || !formData.first_name || !formData.last_name || !formData.branch || !formData.batch_number || !formData.intern_start_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate intern_number format
    const internIdRegex = /^ME\/I-\d{3}$/;
    if (!internIdRegex.test(formData.intern_number)) {
      toast.error("Intern ID must be in format ME/I-XXX (e.g., ME/I-001)");
      return;
    }
    
    try {
      if (editMode) {
        await api.put(`/interns/${currentIntern.id}`, formData);
        toast.success("Intern updated successfully");
      } else {
        await api.post("/interns", formData);
        toast.success("Intern created successfully");
      }
      setDialogOpen(false);
      fetchInterns();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const resetForm = () => {
    setFormData({
      intern_number: "",
      first_name: "",
      last_name: "",
      pseudo_first_name: "",
      pseudo_last_name: "",
      email: "",
      phone: "",
      date_of_birth: "",
      branch: "",
      batch_number: "",
      intern_start_date: "",
      intern_end_date: "",
      completion_status: "Active",
      manager_id: ""
    });
  };

  const handleOpenDeptAssignment = (intern) => {
    setSelectedIntern(intern);
    setDeptAssignmentData({
      department: "",
      team_id: "",
      start_date: new Date().toISOString().split('T')[0],
      notes: ""
    });
    fetchDepartmentHistory(intern.id);
    setDeptAssignmentDialogOpen(true);
  };

  const fetchDepartmentHistory = async (internId) => {
    try {
      const response = await api.get(`/interns/${internId}/department-assignments`);
      setDepartmentHistory(response.data);
    } catch (error) {
      console.error("Failed to fetch department history:", error);
    }
  };

  const handleDeptAssignmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!deptAssignmentData.department || !deptAssignmentData.start_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      await api.post(`/interns/${selectedIntern.id}/department-assignments`, {
        intern_id: selectedIntern.id,
        department: deptAssignmentData.department,
        team_id: deptAssignmentData.team_id || null,
        start_date: deptAssignmentData.start_date,
        notes: deptAssignmentData.notes
      });
      toast.success("Department assigned successfully");
      setDeptAssignmentDialogOpen(false);
      fetchInterns();
      fetchDepartmentHistory(selectedIntern.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign department");
    }
  };

  const handleEndAssignment = async (assignmentId, internId) => {
    const endDate = prompt("Enter end date (YYYY-MM-DD):");
    if (!endDate) return;
    
    try {
      await api.patch(`/interns/${internId}/department-assignments/${assignmentId}/end?end_date=${endDate}`);
      toast.success("Assignment ended successfully");
      fetchDepartmentHistory(internId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to end assignment");
    }
  };

  // Open edit dialog for department assignment (team only)
  const handleOpenEditDeptAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setEditTeamId(assignment.team_id || "");
    setEditDeptDialogOpen(true);
  };

  // Submit team update for department assignment
  const handleUpdateDeptTeam = async (e) => {
    e.preventDefault();
    
    if (!editingAssignment || !viewingIntern) return;
    
    try {
      await api.patch(`/interns/${viewingIntern.id}/department-assignments/${editingAssignment.id}/team`, {
        team_id: editTeamId || null
      });
      toast.success("Team updated successfully");
      setEditDeptDialogOpen(false);
      fetchDepartmentHistory(viewingIntern.id);
      fetchInterns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team");
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      // Call preview endpoint
      const response = await api.post("/import/interns/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setImportPreviewData(response.data);
      setImportPreviewOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview import");
    }
    
    // Reset file input
    e.target.value = "";
  };

  const handleApplyImport = async (applyData) => {
    try {
      const response = await api.post("/import/interns/apply", applyData);
      
      toast.success(`Successfully imported ${response.data.imported} and updated ${response.data.updated} interns!`);
      
      setImportPreviewOpen(false);
      fetchInterns();
    } catch (error) {
      toast.error("Failed to apply import");
    }
  };


  const handleConvertToEmployee = (intern) => {
    setConvertingIntern(intern);
    setEmployeeId("");
    setConvertDialogOpen(true);
  };

  const submitConversion = async () => {
    if (!employeeId.trim()) {
      toast.error("Please enter Employee ID");
      return;
    }

    try {
      await api.post(`/interns/${convertingIntern.id}/convert-to-employee?employee_id=${employeeId}`);
      toast.success("Intern successfully converted to employee");
      setConvertDialogOpen(false);
      fetchInterns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert intern to employee");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Active: "bg-green-100 text-green-800",
      Completed: "bg-blue-100 text-blue-800",
      Released: "bg-yellow-100 text-yellow-800",
      Absconded: "bg-red-100 text-red-800"
    };
    return <Badge className={colors[status] || "bg-md3-surface-container text-md3-on-surface"}>{status}</Badge>;
  };

  // Get unique batches for filter
  const uniqueBatches = [...new Set(interns.map(i => i.batch_number))].sort();
  
  // Filter interns based on tab and filters
  const filteredInterns = interns.filter(intern => {
    // Tab filtering
    if (activeTab === "active" && intern.converted_to_employee_id) return false;
    if (activeTab === "converted" && !intern.converted_to_employee_id) return false;
    
    // Other filters
    if (selectedBatch && selectedBatch !== "all" && intern.batch_number !== selectedBatch) return false;
    if (selectedBranch && selectedBranch !== "all" && intern.branch !== selectedBranch) return false;
    if (selectedStatus && selectedStatus !== "all" && intern.completion_status !== selectedStatus) return false;
    
    return true;
  });

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small">Interns</h1>
            <p className="text-md3-on-surface-variant">Manage intern records and department assignments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
              onClick={async () => {
                try {
                  const response = await api.get("/template/interns", { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'interns_template.csv');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success("Template downloaded successfully");
                } catch (error) {
                  toast.error("Failed to download template");
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <label htmlFor="bulk-upload" className="cursor-pointer">
              <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => document.getElementById('bulk-upload').click()}>
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
            </label>
            <input
              id="bulk-upload"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleBulkUpload}
              className="hidden"
            />
            <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              New Intern
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Batch</Label>
                <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="All Batches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Batches</SelectItem>
                    {uniqueBatches.map(batch => (
                      <SelectItem key={batch} value={batch}>{batch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Branch</Label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.name}>{b.name} ({b.country})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {statuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(selectedBatch || selectedBranch || selectedStatus) && (
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full mt-4" size="sm"
                
                onClick={() => {
                  setSelectedBatch("");
                  setSelectedBranch("");
                  setSelectedStatus("");
                }}
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Interns Table */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">Interns Management</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="active">Active Interns ({interns.filter(i => !i.converted_to_employee_id).length})</TabsTrigger>
                <TabsTrigger value="converted">Converted to Employees ({interns.filter(i => i.converted_to_employee_id).length})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="active">
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : filteredInterns.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-md3-on-surface-variant">No active interns found</p>
                  </div>
                ) : (
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Intern ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Branch</TableHead>
                        <TableHead>Batch</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInterns.map((intern) => (
                    <TableRow key={intern.id}>
                      <TableCell className="font-medium">{intern.intern_number}</TableCell>
                      <TableCell>{intern.first_name} {intern.last_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{intern.current_department}</Badge>
                      </TableCell>
                      <TableCell>{intern.branch}</TableCell>
                      <TableCell>{intern.batch_number}</TableCell>
                      <TableCell>{intern.intern_start_date}</TableCell>
                      <TableCell>{getStatusBadge(intern.completion_status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm"
                            variant="ghost"
                            className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                            onClick={() => handleView(intern)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm"
                            variant="outline"
                            className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                            onClick={() => handleEdit(intern)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          {intern.completion_status === "Completed" && (
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                              onClick={() => handleConvertToEmployee(intern)}
                              title="Convert to Employee"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="sm"
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                            onClick={() => handleDelete(intern.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
              
              <TabsContent value="converted">
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : filteredInterns.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-md3-on-surface-variant">No converted interns found</p>
                  </div>
                ) : (
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Intern ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Department</TableHead>
                        <TableHead>Branch</TableHead>
                        <TableHead>Batch</TableHead>
                        <TableHead>Employee ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInterns.map((intern) => (
                        <TableRow key={intern.id} className="bg-blue-50">
                          <TableCell className="font-medium">{intern.intern_number}</TableCell>
                          <TableCell>{intern.first_name} {intern.last_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{intern.current_department}</Badge>
                          </TableCell>
                          <TableCell>{intern.branch}</TableCell>
                          <TableCell>{intern.batch_number}</TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-800">{intern.converted_to_employee_id}</Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(intern.completion_status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm"
                                variant="ghost"
                                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleView(intern)}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <span className="text-xs text-md3-on-surface-variant self-center">Read-only</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? "Edit Intern" : "Create New Intern"}</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {editMode ? "Update intern information" : "Add a new intern to the system"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Intern ID * (Format: ME/I-XXX)</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.intern_number}
                    onChange={(e) => setFormData({ ...formData, intern_number: e.target.value })}
                    placeholder="ME/I-001"
                    disabled={editMode}
                    required
                  />
                  {editMode && (
                    <p className="text-xs text-md3-on-surface-variant mt-1">Intern ID cannot be changed</p>
                  )}
                </div>
                <div>
                  <Label>First Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Pseudo First Name (Optional)</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.pseudo_first_name}
                    onChange={(e) => setFormData({ ...formData, pseudo_first_name: e.target.value })}
                    placeholder="For user account (if needed)"
                  />
                </div>
                <div>
                  <Label>Pseudo Last Name (Optional)</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.pseudo_last_name}
                    onChange={(e) => setFormData({ ...formData, pseudo_last_name: e.target.value })}
                    placeholder="For user account (if needed)"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Branch *</Label>
                  <Select
                    value={formData.branch}
                    onValueChange={(value) => setFormData({ ...formData, branch: value })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map(b => (
                        <SelectItem key={b.id} value={b.name}>{b.name} ({b.country})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Batch Number * (e.g., 2025-B1)</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.batch_number}
                    onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                    placeholder="2025-B1"
                    required
                  />
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="date"
                    value={formData.intern_start_date}
                    onChange={(e) => setFormData({ ...formData, intern_start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="date"
                    value={formData.intern_end_date}
                    onChange={(e) => setFormData({ ...formData, intern_end_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.completion_status}
                    onValueChange={(value) => setFormData({ ...formData, completion_status: value })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="manager_id" className="text-md3-label-large text-gray-700 font-medium">Manager (Optional)</Label>
                <Select
                  value={formData.manager_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, manager_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {employees
                      .filter(emp => 
                        // Only show employees with manager/admin/team lead access levels
                        emp.access_level === "Manager" || emp.access_level === "Admin" || emp.access_level === "Team Lead"
                      )
                      .map((emp) => (
                        <SelectItem key={emp.id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-md3-on-surface-variant">Manager who will approve timesheets</p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {editMode ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Intern Details</DialogTitle>
            </DialogHeader>
            {viewingIntern && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Intern ID</Label>
                    <p className="font-medium">{viewingIntern.intern_number}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Name</Label>
                    <p className="font-medium">{viewingIntern.first_name} {viewingIntern.last_name}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Email</Label>
                    <p className="font-medium">{viewingIntern.email || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Phone</Label>
                    <p className="font-medium">{viewingIntern.phone || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Date of Birth</Label>
                    <p className="font-medium">{viewingIntern.date_of_birth || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Branch</Label>
                    <p className="font-medium">{viewingIntern.branch}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Batch</Label>
                    <p className="font-medium">{viewingIntern.batch_number}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Current Department</Label>
                    <p className="font-medium">{viewingIntern.current_department}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Start Date</Label>
                    <p className="font-medium">{viewingIntern.intern_start_date}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">End Date</Label>
                    <p className="font-medium">{viewingIntern.intern_end_date || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Status</Label>
                    <div className="mt-1">{getStatusBadge(viewingIntern.completion_status)}</div>
                  </div>
                </div>

                {/* Department History */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-md3-title-large">Department History</h3>
                    <Button size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleOpenDeptAssignment(viewingIntern)}>
                      Change Department
                    </Button>
                  </div>
                  <div className="border rounded-md3-lg p-4">
                    {departmentHistory.length === 0 ? (
                      <p className="text-md3-on-surface-variant text-md3-body-small">No department history</p>
                    ) : (
                      <div className="space-y-3">
                        {departmentHistory.map((assignment) => {
                          const teamName = assignment.team_id 
                            ? teams.find(t => t.id === assignment.team_id)?.team_name 
                            : null;
                          return (
                            <div key={assignment.id} className="border-b pb-3 last:border-b-0">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium">{assignment.department}</p>
                                  {teamName ? (
                                    <p className="text-md3-body-small text-blue-600">Team: {teamName}</p>
                                  ) : (
                                    <p className="text-md3-body-small text-gray-400">No team assigned</p>
                                  )}
                                  <p className="text-md3-body-small text-md3-on-surface-variant">
                                    {assignment.start_date} 
                                    {assignment.end_date ? ` - ${assignment.end_date}` : " - Present"}
                                    {assignment.is_active && <Badge className="ml-2 bg-green-100 text-green-800">Active</Badge>}
                                  </p>
                                  {assignment.notes && (
                                    <p className="text-md3-body-small text-md3-on-surface-variant mt-1">{assignment.notes}</p>
                                  )}
                                </div>
                                {assignment.is_active && (
                                  <div className="flex gap-2">
                                    <Button size="sm"
                                      variant="outline"
                                      className="border-blue-500 text-blue-600 hover:bg-blue-50 transition-all duration-200 rounded-md3-full"
                                      onClick={() => handleOpenEditDeptAssignment(assignment)}>
                                      Edit
                                    </Button>
                                    <Button size="sm"
                                      variant="outline"
                                      className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                      onClick={() => handleEndAssignment(assignment.id, viewingIntern.id)}>
                                      End
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setViewDialogOpen(false)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Department Assignment Dialog (Team Only) */}
        <Dialog open={editDeptDialogOpen} onOpenChange={setEditDeptDialogOpen}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold">Edit Team Assignment</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Update team for {viewingIntern?.first_name} {viewingIntern?.last_name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateDeptTeam} className="space-y-6">
              {/* Read-only Department */}
              <div>
                <Label className="text-gray-500">Department</Label>
                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {editingAssignment?.department}
                </div>
              </div>
              
              {/* Read-only Start Date */}
              <div>
                <Label className="text-gray-500">Start Date</Label>
                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {editingAssignment?.start_date}
                </div>
              </div>
              
              {/* Editable Team */}
              <div>
                <Label className="text-md3-label-large text-gray-700 font-medium">Team</Label>
                <Select
                  value={editTeamId || "none"}
                  onValueChange={(value) => setEditTeamId(value === "none" ? "" : value)}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Team</SelectItem>
                    {teams
                      .filter(team => team.department === editingAssignment?.department)
                      .map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.team_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {editingAssignment?.department && teams.filter(team => team.department === editingAssignment?.department).length === 0 && (
                  <p className="text-xs text-md3-on-surface-variant mt-1">No teams available for {editingAssignment?.department} department</p>
                )}
              </div>
              
              {/* Read-only Notes */}
              {editingAssignment?.notes && (
                <div>
                  <Label className="text-gray-500">Notes</Label>
                  <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                    {editingAssignment?.notes}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setEditDeptDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                  Update Team
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Department Assignment Dialog */}
        <Dialog open={deptAssignmentDialogOpen} onOpenChange={setDeptAssignmentDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Change Department</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Assign {selectedIntern?.first_name} {selectedIntern?.last_name} to a new department
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDeptAssignmentSubmit} className="space-y-6">
              <div>
                <Label>Department *</Label>
                <Select
                  value={deptAssignmentData.department}
                  onValueChange={(value) => setDeptAssignmentData({ ...deptAssignmentData, department: value, team_id: "" })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Team (Optional)</Label>
                <Select
                  value={deptAssignmentData.team_id || "none"}
                  onValueChange={(value) => setDeptAssignmentData({ ...deptAssignmentData, team_id: value === "none" ? "" : value })}
                  disabled={!deptAssignmentData.department}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Team</SelectItem>
                    {teams
                      .filter(team => team.department === deptAssignmentData.department)
                      .map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.team_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {deptAssignmentData.department && teams.filter(team => team.department === deptAssignmentData.department).length === 0 && (
                  <p className="text-xs text-md3-on-surface-variant mt-1">No teams available for {deptAssignmentData.department} department</p>
                )}
              </div>
              <div>
                <Label>Start Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="date"
                  value={deptAssignmentData.start_date}
                  onChange={(e) => setDeptAssignmentData({ ...deptAssignmentData, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={deptAssignmentData.notes}
                  onChange={(e) => setDeptAssignmentData({ ...deptAssignmentData, notes: e.target.value })}
                  placeholder="Optional notes about the assignment"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setDeptAssignmentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >Assign</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Results Dialog */}
        <Dialog open={bulkUploadDialogOpen} onOpenChange={setBulkUploadDialogOpen}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Bulk Upload Results</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div>
                <p className="text-lg">
                  <span className="font-semibold text-green-600">{uploadSuccessCount}</span> interns imported successfully
                </p>
              </div>
              {uploadErrors.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 text-red-600">Errors ({uploadErrors.length}):</h4>
                  <div className="max-h-60 overflow-y-auto border rounded p-3 space-y-2">
                    {uploadErrors.map((error, index) => (
                      <div key={index} className="text-md3-body-small border-b pb-2">
                        <span className="font-medium">Row {error.row}:</span> {error.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end pt-4">
                <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => setBulkUploadDialogOpen(false)}>Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        {/* Convert to Employee Dialog */}
        <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Convert Intern to Employee</DialogTitle>
            </DialogHeader>
            {convertingIntern && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-md3-lg p-4 space-y-2">
                  <p className="text-md3-body-small text-md3-on-surface-variant">Converting Intern:</p>
                  <p className="font-semibold text-lg">
                    {convertingIntern.intern_number} - {convertingIntern.first_name} {convertingIntern.last_name}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-md3-body-small mt-2">
                    <div>
                      <span className="text-md3-on-surface-variant">Branch:</span>
                      <p className="font-medium">{convertingIntern.branch}</p>
                    </div>
                    <div>
                      <span className="text-md3-on-surface-variant">Department:</span>
                      <p className="font-medium">{convertingIntern.current_department}</p>
                    </div>
                    <div>
                      <span className="text-md3-on-surface-variant">Batch:</span>
                      <p className="font-medium">{convertingIntern.batch_number}</p>
                    </div>
                    <div>
                      <span className="text-md3-on-surface-variant">Status:</span>
                      <p className="font-medium">{convertingIntern.completion_status}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employee_id" className="text-md3-label-large text-gray-700 font-medium">Employee ID *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="employee_id"
                    value={employeeId}
                    onChange={(e) => setEmployeeId(e.target.value)}
                    placeholder="Enter Employee ID (e.g., EMP001)"
                    required
                  />
                  <p className="text-xs text-md3-on-surface-variant">
                    This will be the new employee's unique identifier
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md3-lg p-3">
                  <p className="text-md3-body-small text-yellow-800">
                    <strong>Note:</strong> The employee record will be created with the intern's details. 
                    A back-reference to the intern ID will be maintained for tracking.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setConvertDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={submitConversion}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create Employee
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Import Preview Dialog */}
        <BulkImportPreview
          open={importPreviewOpen}
          onClose={() => setImportPreviewOpen(false)}
          previewData={importPreviewData}
          onApply={handleApplyImport}
          entityType="Interns"
        />

      </div>
    </Layout>
  );
}
